import type { AtLeast } from '.'
import {
  Block,
  ComponentBlock,
  InstanceBlock,
  DocumentBlock,
} from '../schema/block'
import {
  isComponentBlock,
  isHidableBlock,
  isInstanceBlock,
  isListBlock,
  isReferenceBlock,
  isRichTextBlock,
} from '../schema/type-casts'
import { normalizeStringPath } from './normalize-string-path'

export const shouldForcefullyPropagateHiddenState = (
  block: AtLeast<Block, 'type'>
): block is ComponentBlock | InstanceBlock | DocumentBlock => {
  return (
    isInstanceBlock(block) || isComponentBlock(block) || isRichTextBlock(block)
  )
}

/** In summary, the hide mode is more restrictive than the show mode. */
export function shouldInheritHiddenState(
  block: AtLeast<Block, 'type' | 'id'>,
  path: string,
  getBlock: (id: string) => Block | null | undefined
): block is ComponentBlock | InstanceBlock | DocumentBlock {
  let shouldBypassInstanceForcedState = false
  const pathReversedArray = normalizeStringPath({
    path,
    edgeId: block.id,
    format: 'array',
    includeEdge: false,
    includeRoot: false,
  }).reverse()

  for (const p of pathReversedArray) {
    const parentBlock = getBlock(p)
    if (!parentBlock) return false
    if (isListBlock(parentBlock) || isReferenceBlock(parentBlock)) {
      shouldBypassInstanceForcedState = true
    } else if (
      (!shouldBypassInstanceForcedState &&
        shouldForcefullyPropagateHiddenState(parentBlock)) ||
      parentBlock.hidden
    ) {
      return isHidableBlock(block)
    }
  }

  return false
}

export async function resolveInheritedHiddenState(
  block: AtLeast<Block, 'type' | 'id'>,
  path: string,
  getBlock: (id: string) => Promise<Block | null | undefined>
): Promise<
  { shouldInherit: true; valueToInherit: boolean } | { shouldInherit: false }
> {
  const resolvedBlocks = await Promise.all(
    normalizeStringPath({
      path,
      edgeId: block.id,
      format: 'array',
      includeEdge: false,
      includeRoot: false,
    })
      .reverse()
      .map(getBlock)
  )

  let shouldBypassInstanceForcedState = false
  for (const parentBlock of resolvedBlocks) {
    if (!parentBlock) {
      // don't throw... parent might be virtualized
      continue
    }
    if (isListBlock(parentBlock) || isReferenceBlock(parentBlock)) {
      shouldBypassInstanceForcedState = true
    } else if (
      (!shouldBypassInstanceForcedState &&
        shouldForcefullyPropagateHiddenState(parentBlock) &&
        isHidableBlock(parentBlock)) ||
      parentBlock.hidden
    ) {
      return {
        shouldInherit: isHidableBlock(block),
        valueToInherit: parentBlock.hidden,
      }
    }
  }

  return { shouldInherit: false }
}
